import { CssBaseline, ThemeProvider as Provider, createTheme } from '@mui/material';

interface ThemeProviderProps {
  children: React.ReactNode;
}

const theme = createTheme({
  typography: {
    fontFamily: "'Montserrat', sans-serif",
    h1: { fontWeight: 700, fontSize: '2.5rem', letterSpacing: '-0.01em' },
    h2: { fontWeight: 600, fontSize: '1.875rem', letterSpacing: '-0.01em' },
    h3: { fontWeight: 600, fontSize: '1.375rem' },
    h4: { fontWeight: 500, fontSize: '1.125rem' },
    h5: { fontWeight: 500, fontSize: '1rem' },
    h6: { fontWeight: 500, fontSize: '0.875rem' },
    body1: { fontSize: '1rem', lineHeight: 1.75 },
    body2: { fontSize: '0.875rem', lineHeight: 1.65 },
    subtitle2: { fontSize: '0.8125rem', lineHeight: 1.6 },
  },
  palette: {
    primary: {
      main: '#2D5050',
      light: '#3D6B6B',
      dark: '#1A3535',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#B8965A',
      light: '#D4B07E',
      dark: '#8A6F3E',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFAF8',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1C1C1C',
      secondary: '#6B7280',
    },
    error: { main: '#B40000' },
    warning: { main: '#D58600' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: '#1C1C1C',
          backgroundColor: '#FAFAF8',
        },
        'div.MuiBox-root': {
          scrollbarColor: '#2D5050 transparent',
          '&::-webkit-scrollbar': { width: '5px', height: '5px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#2D5050',
            borderRadius: '5px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          minHeight: '44px',
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontWeight: 600,
          fontFamily: "'Montserrat', sans-serif",
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          backgroundColor: '#2D5050',
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#1A3535' },
        },
        outlined: {
          borderColor: '#2D5050',
          color: '#2D5050',
          '&:hover': {
            backgroundColor: '#2D5050',
            borderColor: '#2D5050',
            color: '#FFFFFF',
          },
        },
        text: {
          color: '#2D5050',
          '&:hover': { backgroundColor: 'rgba(45, 80, 80, 0.08)' },
        },
      },
    },
    MuiContainer: {
      defaultProps: { maxWidth: 'lg' },
    },
  },
});

export const ThemeProvider = ({ children }: ThemeProviderProps) => (
  <Provider theme={theme}>
    <CssBaseline />
    {children}
  </Provider>
);
